import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import CompanyLogo from '../components/common/CompanyLogo';
import AuthenticatedNavigation from '../components/common/AuthenticatedNavigation';
import { useAuth } from '../hooks/useAuth';

interface EligibilitySuccessProps {
  results: any;
  onUpdateState: (updates: any) => void;
}

const EligibilitySuccess: React.FC<EligibilitySuccessProps> = ({ onUpdateState }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const results = location.state?.results || location.state?.eligibilityResults || {};

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleProceed = () => {
    onUpdateState({ eligibilityResults: results });
    navigate('/deal-options');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Matching Landing Page */}
      <header className="bg-white border-b border-gray-200">
        {/* Top Utility Bar */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-end items-center h-10 text-sm">
              {/* <a href="#demo" className="text-gray-700 hover:text-navy-700 ml-6">Schedule a Demo</a> */}
              <a href="#support" className="text-gray-700 hover:text-navy-700 ml-6">Customer Service</a>
              {isAuthenticated ? (
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="ml-6 text-navy-700 font-medium hover:text-navy-900"
                >
                  Dashboard
                </button>
              ) : (
                <button 
                  onClick={() => navigate('/login')}
                  className="ml-6 text-navy-700 font-medium hover:text-navy-900"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            {/* Logo */}
            <AuthenticatedNavigation className="flex items-center cursor-pointer">
              <img src="/24lp-logo-primary.png" alt="Twenty Four Capital" className="h-20 w-auto" />
            </AuthenticatedNavigation>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/#how-it-works" className="text-gray-700 hover:text-navy-700 font-medium">How it Works</a>
              <a href="/#platform-features" className="text-gray-700 hover:text-navy-700 font-medium">x</a>
              <a href="/#why-us" className="text-gray-700 hover:text-navy-700 font-medium">Why Choose Us</a>
            </div>

            {/* CTA Button */}
            {isAuthenticated ? (
              <button 
                onClick={() => navigate('/dashboard')}
                className="bg-orange-500 text-white px-6 py-2.5 rounded-md font-medium hover:bg-orange-600 transition-colors"
              >
                Dashboard
              </button>
            ) : (
              <button 
                onClick={() => navigate('/')}
                className="bg-orange-500 text-white px-6 py-2.5 rounded-md font-medium hover:bg-orange-600 transition-colors"
              >
                Home
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Header */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircleIcon className="h-12 w-12 text-green-600" />
            </div>
            
            {/* Company Logo and Name */}
            {results.ticker && (
              <div className="flex justify-center items-center gap-4 mb-6">
                <CompanyLogo 
                  ticker={results.ticker} 
                  companyName={results.companyName} 
                  size="xl" 
                />
                <div className="text-left">
                  <h2 className="text-2xl font-bold text-gray-900">{results.companyName}</h2>
                  <p className="text-lg text-gray-600">{results.ticker}</p>
                </div>
              </div>
            )}
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Congratulations! You're Eligible
            </h1>
            <p className="text-xl text-gray-600">
              Your company qualifies for our equity financing facility
            </p>
          </motion.div>

          {/* Company Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-8 mb-8"
          >
            <div className="flex items-center mb-6">
              <BuildingOfficeIcon className="h-8 w-8 text-gray-600 mr-3" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {results.companyName || 'Your Company'} ({results.ticker})
                </h2>
                <p className="text-gray-600">
                  Market Cap: {results.marketCap ? formatCurrency(results.marketCap) : 'N/A'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-600 mr-2" />
                  <h3 className="font-semibold text-green-900">Maximum Facility Size</h3>
                </div>
                <p className="text-3xl font-bold text-green-900">
                  {results.maxFacilitySize ? formatCurrency(results.maxFacilitySize) : 'N/A'}
                </p>
                <p className="text-sm text-green-700 mt-2">
                  Available credit line based on current market conditions
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-blue-900">Eligibility Status</h3>
                </div>
                <p className="text-3xl font-bold text-blue-900">Approved</p>
                <p className="text-sm text-blue-700 mt-2">
                  Your company meets all requirements
                </p>
              </div>
            </div>
          </motion.div>

          {/* Available Options */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-8 mb-8"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Available Financing Options
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DocumentTextIcon className="h-8 w-8 text-orange-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">VWAP Exercise</h4>
                <p className="text-sm text-gray-600">
                  Standard market-based pricing with flexible terms
                </p>
              </div>

              <div className="text-center">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClockIcon className="h-8 w-8 text-orange-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Rapid Exercise</h4>
                <p className="text-sm text-gray-600">
                  Quick access to capital when you need it most
                </p>
              </div>

              <div className="text-center">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CurrencyDollarIcon className="h-8 w-8 text-orange-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Advance Draw</h4>
                <p className="text-sm text-gray-600">
                  Same-day funding for immediate capital needs
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={handleProceed}
                className="flex-1 bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center"
              >
                View Deal Options
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </button>
              <button 
                onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/')}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                {isAuthenticated ? 'Back to Dashboard' : 'Back to Home'}
              </button>
            </div>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What Happens Next?
            </h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div className="flex items-center">
                <div className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3">1</div>
                <span className="text-gray-700">Review deal options</span>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-gray-400 hidden md:block" />
              <div className="flex items-center">
                <div className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3">2</div>
                <span className="text-gray-700">Select your terms</span>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-gray-400 hidden md:block" />
              <div className="flex items-center">
                <div className="bg-navy-700 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3">3</div>
                <span className="text-gray-700">Complete application</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EligibilitySuccess; 